import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import produce from 'immer'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { Message } from 'primereact/message'

import { useToastContext } from '../../../../../../contexts'
import { OrderResponseItem } from '../../../../../../data/models'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import { isItemInitial } from '../../../../../../utils/helper/checkConfirmationCodes'
import { prepareCompletePatchDto } from '../../../../../../utils/helper/prepareCompletePatchDto'
import {
  ConfigurableComponent,
  ConfigurableTable,
  convertOrderItems,
} from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config'
import {
  requestCompleteOrderById,
  requestSendOrderById,
  useOrder,
  useOrderItems,
} from '../../../../data'

interface Props {
  visible: boolean
  preliminary: boolean
  setVisible: (params: boolean) => void
  id: string
  currency: string
}

export const Accept: React.FC<Props> = ({
  visible,
  preliminary,
  setVisible,
  id,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const navigate = useNavigate()

  const { data: dataOrder } = useOrder(id)
  const { data: itemsData, mutate, isLoading } = useOrderItems(id)

  const { uiConfig: config } = useConfigContext()

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  // ************************************************************
  // functions
  // ************************************************************
  const handleClose = () => {
    setVisible(false)
  }

  const submitAcceptOrder = async () => {
    // deactivate the buttons
    setIsRequestRunning(true)

    try {
      // close the dialog
      handleClose()

      // make a copy of OrderResponse
      const newOrderResponse = produce(dataOrder!, draft => {
        // set confirmationDate
        draft.Payload.ConfirmationDate = new Date().toISOString()

        // check if an item was modified or rejected
        const isItemModified = (item: OrderResponseItem) =>
          item.ConfirmationCode === 'MODIFIED' ||
          item.ConfirmationCode === 'REJECTED'

        draft.Payload.ConfirmationCode = itemsData?.some(isItemModified)
          ? 'MODIFIED'
          : 'ACCEPTED'
      })

      const patchDto = prepareCompletePatchDto(newOrderResponse, dataOrder!)

      if (preliminary) {
        await requestSendOrderById(id, patchDto)
      } else {
        await requestCompleteOrderById(id, patchDto)
      }

      mutate()
      // navigate back to the start page
      navigate('/')

      // show success toast
      showToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t(
          'orderResponse.title.send.dialog.send.messages.success.summary',
        ),
        detail: preliminary
          ? undefined
          : t('orderResponse.title.send.dialog.send.messages.success.detail', {
              orderId: dataOrder?.Payload.OrderId,
            }),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t(
          'orderResponse.title.send.dialog.send.messages.failure.summary',
          {
            orderId: dataOrder?.Payload.OrderId,
          },
        ),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  // ************************************************************
  // elements
  // ************************************************************
  // header
  const header = (
    <>
      {preliminary
        ? t('orderResponse.title.send.dialog.title_prelim')
        : t('orderResponse.title.send.dialog.title')}
    </>
  )

  // footer
  const footer = (
    <>
      <Button
        label={t('orderResponse.title.send.dialog.cancel.label')}
        icon="pi pi-times"
        className="p-button-text w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
      />
      <Button
        label={t('orderResponse.title.send.dialog.send.label')}
        icon={'pi pi-check'}
        className="w-full md:w-auto"
        onClick={submitAcceptOrder}
        loading={isRequestRunning}
      />
    </>
  )

  // confirmationId
  const confirmationIdLabel = (
    <label className="text-sm" htmlFor={'confirmationId'}></label>
  )
  const confirmationIdInput = (
    <InputText
      id={'confirmationId'}
      value={dataOrder?.Payload.ConfirmationId!}
      className="w-full"
      disabled
    />
  )

  const confirmationIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.acceptOrder}
      component={confirmationIdInput}
      label={confirmationIdLabel}
      className="col-12 md:col-6 lg:col-6"
    />
  )

  // confirmationDate
  const confirmationDateLabel = (
    <label className="text-sm" htmlFor={'confirmationDate'}></label>
  )
  const confirmationDateInput = (
    <Calendar
      id={'confirmationDate'}
      value={new Date()}
      className="w-full"
      readOnlyInput
      showIcon
      locale={language}
      disabled
    />
  )

  const confirmationDateWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.acceptOrder}
      component={confirmationDateInput}
      label={confirmationDateLabel}
      className="col-12 md:col-6 lg:col-6"
    />
  )

  return (
    <>
      <Dialog
        visible={visible}
        header={header}
        style={{ width: '60rem' }}
        breakpoints={{ [calculateBreakpointWidth(60)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <div className="formgrid grid">
          {/* confirmationId */}
          {confirmationIdWrapper}

          {/* confirmationDate */}
          {confirmationDateWrapper}
        </div>

        <h3>{t('orderResponse.title.send.dialog.orderOverview.title')}</h3>
        {isItemInitial(itemsData!) && (
          <Message
            className="mb-3"
            text={t('orderResponse.title.send.dialog.orderOverview.message')}
          />
        )}
        <ConfigurableTable
          config={config.orderResponse.acceptOrder.table}
          data={convertOrderItems(itemsData)}
          isLoading={isLoading}
          stateKey="table-accept-order"
        />
      </Dialog>
    </>
  )
}
